import { action, computed, observable } from 'mobx'
import { type Task, task } from 'mobx-task'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type {
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetTeamMemberFn,
  SearchMembersFn,
  ListTeamMembersForWorkspaceMemberFn,
} from '@/features/iam'
import type { Permissions } from '@/features/iam/permissions/Permissions'
import { type SelectFilter } from '@/infra/SelectFilter'
import { type ActivationLifecycle } from '@/infra/activation'
import type { Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { SelectionState } from '@/infra/selection-state'
import { tapAction, toStream } from '@/utils/mobx-util'
import { match } from '@/utils/pattern-match-util'
import { type JobStore } from '@/features/jobs/JobStore'
import { type ProviderStore } from '@/features/jobs/ProviderStore'
import { type CreateJobDialogViewModel as JobCreationDialogViewModel } from '@/features/jobs/drafting/components/CreateJobDialog/CreateJobDialogViewModel'
import { TransferJobDialogViewModel } from '@/features/jobs/TransferJobDialog/TransferJobDialogViewModel'
import { type SearchableJobStore } from '@/features/jobs/list/SearchableJobStore'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { BulkActionJobErrorDialogViewModel } from '@/features/jobs/list/components/BulkActionJobErrorDialog/BulkActionJobErrorDialogViewModel'
import { BulkCopyJobsDialogViewModel } from '@/features/jobs/list/components/BulkCopyJobsDialog/BulkCopyJobsDialogViewModel'
import { BulkCopyJobsSuccessDialogViewModel } from '@/features/jobs/list/components/BulkCopyJobsSuccessDialog/BulkCopyJobsSuccessDialogViewModel'
import { AdvanceJobsDialogViewModel } from '@/features/jobs/list/components/AdvanceJobsDialog/AdvanceJobsDialogViewModel'

/**
 * States permitted for the Archive/Restore Jobs Action. Currently, this allows multi-selecting
 * jobs to archive or restore.
 */
export type ArchiveRestoreAction = Archive | Restore | NoJobsSelected

/**
 * Indicates that jobs should be archived.
 */
export interface Archive {
  type: 'Archive'
}

/**
 * Indicates that jobs should be restored.
 */
export interface Restore {
  type: 'Restore'
}

/**
 * Indicates that no jobs are selected.
 */
export interface NoJobsSelected {
  type: 'NoJobsSelected'
}

/**
 * Handles the selection logic and the available actions.
 */
export class JobManagementSelectionViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Lists the jobs a bulk action failed for.
   */
  readonly bulkActionErrorVm = new BulkActionJobErrorDialogViewModel()

  /**
   * Shown once a bulk copy succeeds.
   */
  readonly bulkCopyJobsSuccessVm

  /**
   * Confirms copying the selected jobs.
   */
  readonly bulkCopyVm

  /**
   * Transfers the selected jobs to another client or pro.
   */
  readonly transferJobVm

  /**
   * Confirms advancing the selected pending jobs.
   */
  readonly advanceJobsVm

  /**
   * The jobs selected for bulk actions.
   */
  readonly selectedJobs = observable.set<SearchableJob>()

  constructor(
    private readonly _getFilteredJobs: () => readonly SearchableJob[],
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _jobStore: JobStore,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _getSelectedWorkspaces: () => Id[],
    private readonly _getShowArchived: () => boolean,
    private readonly _removeJobs: (ids: Id[]) => void,
    private readonly _searchableJobStore: SearchableJobStore,
    private readonly _pushJob: (job: SearchableJob) => void,
    private readonly _fetchAllJobs: Task<[], Promise<void>>,
    private readonly _getWorkspacesFilter: () => SelectFilter<string, Id>,
    private readonly _findMembers: SearchMembersFn,
    private readonly _findTeamMembersByUserPublicId: ListTeamMembersForWorkspaceMemberFn,
    private readonly _providerStore: ProviderStore,
    private readonly _jobCreationDialogViewModel: JobCreationDialogViewModel,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getTeamMember: GetTeamMemberFn,
  ) {
    this.archiveRestoreJobs = this.archiveRestoreJobs.bind(this)

    this.bulkCopyJobsSuccessVm = new BulkCopyJobsSuccessDialogViewModel(
      this._jobCreationDialogViewModel,
    )

    this.bulkCopyVm = new BulkCopyJobsDialogViewModel(
      this._searchableJobStore,
      this._newAlert,
      this._createAlertErrorHandler,
      (jobIds) => this._jobStore.bulkCopyJobs(jobIds),
      this._pushJob,
      this.bulkActionErrorVm,
      this.bulkCopyJobsSuccessVm,
    )

    this.transferJobVm = new TransferJobDialogViewModel(
      this._findMembers,
      this._findTeamMembersByUserPublicId,
      this._jobStore,
      this._providerStore,
      this._newAlert,
      this._getCurrentMember,
      undefined,
      this.bulkActionErrorVm,
    )

    this.advanceJobsVm = new AdvanceJobsDialogViewModel(
      this._jobStore,
      this._newAlert,
      this._createAlertErrorHandler,
      () => Array.from(this.selectedJobs.values()),
      this.clearSelectedJobsForActions,
      this.bulkActionErrorVm,
    )

    this._lifecycle
      .manage(this.bulkCopyVm)
      // Clear selected jobs when workspaces change so that we don't bring over old selections.
      .manage(
        toStream(() => this.selectedWorkspaces).pipe(
          tapAction(() => this.clearSelectedJobsForActions()),
        ),
      )
      // Clear selected jobs when the active filter changes so that we don't bring over old selections.
      .manage(
        toStream(() => this.showArchived).pipe(
          tapAction(() => this.clearSelectedJobsForActions()),
        ),
      )
  }

  /**
   * The jobs currently listed.
   */
  @computed
  get filteredJobs() {
    return this._getFilteredJobs()
  }

  /**
   * The workspaces the list is filtered to; empty means all of them.
   */
  @computed
  get selectedWorkspaces() {
    return this._getSelectedWorkspaces()
  }

  /**
   * Whether the list shows archived jobs instead of active ones.
   */
  @computed
  get showArchived() {
    return this._getShowArchived()
  }

  /**
   * Whether every job in the list is being loaded so they can all be
   * selected.
   */
  @computed
  get isSelectAllPending() {
    return this._fetchAllJobs.pending
  }

  /**
   * Whether the user is permitted to view archived jobs.
   */
  @computed
  get canViewArchivedJobs() {
    return this._isAllowedInSelectedWorkspaces((p) => p.adminJobList)
  }

  /**
   * Whether the user may archive or restore jobs in the selected workspaces.
   */
  @computed
  get canArchiveRestoreJobs() {
    return this._isAllowedInSelectedWorkspaces((p) => p.adminJobUpdate)
  }

  /**
   * Returns whether scroller items are all selected, none selected, or in between.
   */
  @computed
  get jobActionSelectionState(): SelectionState {
    if (this.selectedJobs.size === 0) {
      return 'NoneSelected'
    }

    if (this.filteredJobs.length === this.selectedJobs.size) {
      return 'AllSelected'
    }

    if (this.selectedJobs.size > 0) {
      return 'SomeSelected'
    }

    return 'NoneSelected'
  }

  /**
   * Indicates the action to take for arching/restoring.
   */
  @computed
  get archiveRestoreAction(): ArchiveRestoreAction {
    if (this.selectedJobs.size === 0) {
      return { type: 'NoJobsSelected' }
    }

    return Array.from(this.selectedJobs.values()).some((job) => job.archived)
      ? { type: 'Restore' }
      : { type: 'Archive' }
  }

  /**
   * The archive or restore action the menu offers, or `null` when the
   * selection can be neither archived nor restored.
   */
  @computed
  get permittedArchiveRestoreAction(): 'Archive' | 'Restore' | null {
    const type = this.archiveRestoreAction.type
    return type !== 'NoJobsSelected' && this.canArchiveRestoreJobs ? type : null
  }

  /**
   * Whether the user may copy every selected job.
   */
  @computed
  get canCloneJobs(): boolean {
    return Array.from(this.selectedJobs.values()).every((job) => {
      if (!job.isDraft) {
        const { permissions } = this._getCurrentMember(job.workspace.id)
        if (permissions.adminJobCreate || permissions.startNewJob) {
          return true
        }
      }

      return this._getTeamMember(
        job.clientTeamId,
        this._getCurrentMember().user.publicId,
      )?.permissions.createTeamJobs
    })
  }

  /**
   * Whether the user may transfer every selected job.
   */
  @computed
  get canTransferJobs(): boolean {
    return Array.from(this.selectedJobs.values()).every((job) => {
      if (job.status === 'Closed') {
        return false
      }

      const { permissions } = this._getCurrentMember(job.workspace.id)
      return (
        permissions.adminJobTransfer &&
        permissions.adminUserList &&
        permissions.adminUserRead &&
        permissions.adminJobView
      )
    })
  }

  /**
   * Whether every selected job is pending and can be advanced by the current
   * member.
   */
  @computed
  get canAdvanceJobs(): boolean {
    return Array.from(this.selectedJobs.values()).every(
      (job) =>
        job.status === 'Pending' &&
        this._getCurrentMember(job.workspace.id).permissions.adminJobUpdate,
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Asks to confirm copying the selected jobs.
   */
  @action.bound
  cloneJobs() {
    this.bulkCopyVm.show(
      Array.from(this.selectedJobs.values()).map((x) => ({
        id: x.id,
        shortId: x.shortId,
        displayName: x.displayName,
      })),
    )
  }

  /**
   * Archives the selected jobs, or restores them when any is archived.
   */
  async archiveRestoreJobs() {
    const ids = Array.from(this.selectedJobs.values()).map((x) => x.id)
    const type = this.archiveRestoreAction.type
    if (type === 'NoJobsSelected') {
      return
    }

    const succeededIds = await match(type, {
      Archive: () =>
        this._jobStore
          .archiveJobs(ids)
          .then((res) => res.archived)
          .catch(this._createAlertErrorHandler('Archiving jobs failed')),
      Restore: () =>
        this._jobStore
          .restoreJobs(ids)
          .then((res) => res.restored)
          .catch(this._createAlertErrorHandler('Restoring jobs failed')),
    })
    this._removeJobs(succeededIds)
    this.clearSelectedJobsForActions()

    if (succeededIds.length !== ids.length) {
      this._newAlert()
        .withState('error')
        .saying(
          `Some jobs could not be ${type === 'Archive' ? 'archived' : 'restored'}. Try again and check permissions.`,
        )
        .show()
    }
  }

  /**
   * Opens the transfer dialog for the selected jobs.
   */
  transferJobs = task.resolved(async () => {
    const ids = Array.from(this.selectedJobs.values()).map((x) => x.id)
    const signal = this._lifecycle.signal
    const withRetry = this._createAlertRetryWrapper(
      'Loading the selected jobs failed',
      signal,
    )
    const jobs = await withRetry(() =>
      this._jobStore.fetchJobs(ids, signal),
    ).catch(() => null)
    if (jobs) {
      this.transferJobVm.open(jobs)
    }
  })

  /**
   * Add job to selected jobs.
   */
  @action.bound
  addJobToSelectedJobs(job: SearchableJob) {
    this.selectedJobs.add(job)
  }

  /**
   * Toggles whether all jobs are selected.
   */
  @action.bound
  toggleAllSelectedJobsForActions() {
    switch (this.jobActionSelectionState) {
      case 'NoneSelected':
      case 'SomeSelected':
        if (this._getWorkspacesFilter().selected.length !== 1) {
          this.filteredJobs.forEach((job) => this.addJobToSelectedJobs(job))
          break
        }

        this._fetchAllJobs()
          .then(() =>
            this.filteredJobs.forEach((job) => this.addJobToSelectedJobs(job)),
          )
          // The list's retry alert already reported a failure.
          .catch(() => undefined)
        break
      case 'AllSelected':
        this.clearSelectedJobsForActions()
        break
    }
  }

  /**
   * Clears the actions selection.
   */
  @action.bound
  clearSelectedJobsForActions() {
    this.selectedJobs.clear()
  }

  /**
   * Indicates whether a job is selected for actions.
   */
  isSelectedForActions(job: SearchableJob) {
    return this.selectedJobs.has(job)
  }

  /**
   * Toggles whether the job is selected for actions.
   */
  @action.bound
  toggleJobSelectionForActions(job: SearchableJob) {
    if (this.isSelectedForActions(job)) {
      this.selectedJobs.delete(job)
    } else {
      this.selectedJobs.add(job)
    }
  }

  private _isAllowedInSelectedWorkspaces(
    isAllowed: (permissions: Permissions) => boolean,
  ) {
    const currentMemberships = this._getCurrentUserWorkspaceMemberships()

    if (this.selectedWorkspaces.length === 0) {
      return currentMemberships.every((m) => isAllowed(m.member.permissions))
    }

    return this.selectedWorkspaces.every((id) => {
      const member = currentMemberships.find(
        (m) => m.workspace.id === id,
      )?.member
      return member && isAllowed(member.permissions)
    })
  }
}
